import { Controller } from "@hotwired/stimulus"
import PhotoSwipe from "photoswipe"

function zoomToFitViewport(zoomLevel) {
  const pan = zoomLevel?.panAreaSize
  const el = zoomLevel?.elementSize
  if (!pan || !el) return 1
  if (!Number.isFinite(pan.x) || !Number.isFinite(pan.y) || !Number.isFinite(el.x) || !Number.isFinite(el.y)) return 1
  if (el.x <= 0 || el.y <= 0) return 1

  // PhotoSwipe's built-in `fit` is hard-capped to <= 1 internally.
  // We want fit-to-viewport regardless of source resolution, so we compute it ourselves.
  return Math.min(pan.x / el.x, pan.y / el.y)
}

function zoomToFillViewport(zoomLevel) {
  const pan = zoomLevel?.panAreaSize
  const el = zoomLevel?.elementSize
  if (!pan || !el) return 1
  if (!Number.isFinite(pan.x) || !Number.isFinite(pan.y) || !Number.isFinite(el.x) || !Number.isFinite(el.y)) return 1
  if (el.x <= 0 || el.y <= 0) return 1

  return Math.max(pan.x / el.x, pan.y / el.y)
}

// PhotoSwipe lightbox for page photos.
//
// Expects:
// - data-page-photo-lightbox-items-value: JSON array of PhotoSwipe SlideData items
//
// Usage:
// - Add `data-action="click->page-photo-lightbox#open"` to any link/button
// - Provide `data-page-photo-lightbox-index-param="0"` (Stimulus action param)
//
// We use PhotoSwipe directly (not PhotoSwipeLightbox) so we can open a single
// unified gallery even when thumbnails are rendered in multiple places (main
// photo + gallery grid).
export default class extends Controller {
  static values = {
    items: { type: Array, default: [] },
  }

  open(event) {
    event?.preventDefault?.()

    const index = Number(event?.params?.index)
    if (!Number.isFinite(index)) return

    const items = Array.isArray(this.itemsValue) ? this.itemsValue : []
    if (items.length === 0) return
    if (index < 0 || index >= items.length) return

    // Provide a thumbnail element for the clicked slide so PhotoSwipe can do
    // a nice zoom animation from the clicked image when possible.
    const clickedEl = event?.currentTarget
    const thumbEl = clickedEl?.querySelector?.("img") || clickedEl

    const dataSource = items.map((item, idx) => {
      if (idx !== index) return item
      return { ...item, element: thumbEl }
    })

    const pswp = new PhotoSwipe({
      dataSource,
      index,
      bgOpacity: 0.92,
      showHideAnimationType: "zoom",
      // Fit-to-viewport regardless of underlying image resolution (allows upscaling).
      initialZoomLevel: zoomToFitViewport,
      // Zoom button / double-tap zooms to "fill" (can also upscale/crop).
      secondaryZoomLevel: zoomToFillViewport,
      // Preload nearby images so paging doesn't flash while downloading.
      preload: [1, 2],
    })

    pswp.on("uiRegister", () => {
      pswp.ui?.registerElement?.({
        name: "custom-caption",
        order: 9,
        isButton: false,
        appendTo: "root",
        html: "",
        onInit: (el) => {
          el.classList.add("pswp__custom-caption")

          const render = () => {
            const data = pswp.currSlide?.data || {}
            const html = data.captionHTML || ""
            el.innerHTML = html
            el.style.display = html ? "" : "none"
          }

          pswp.on("change", render)
          render()
        },
      })
    })

    pswp.init()
  }
}
